/**
 * Renders a typed input/display widget for a database column.
 */
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { fmtDate } from "@/lib/dates";
import { DatePicker } from "@/components/DatePicker";

export function FieldInput({ column, value, onChange, testIdPrefix = "field" }) {
  const t = column.type || "text";
  const tid = `${testIdPrefix}-${column.key}`;
  if (t === "longtext") {
    return (
      <Textarea
        rows={3}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 rounded-sm"
        data-testid={tid}
      />
    );
  }
  if (t === "date" || t === "reminder") {
    return (
      <div className="mt-1">
        <DatePicker
          value={value || ""}
          onChange={(v) => onChange(v)}
          testId={tid}
        />
      </div>
    );
  }
  if (t === "number") {
    return (
      <Input
        type="number"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className="mt-1 rounded-sm font-mono-data"
        data-testid={tid}
      />
    );
  }
  if (t === "url") {
    return (
      <Input
        type="url"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="https://…"
        className="mt-1 rounded-sm"
        data-testid={tid}
      />
    );
  }
  if (t === "email") {
    return (
      <Input
        type="email"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="name@example.com"
        className="mt-1 rounded-sm"
        data-testid={tid}
      />
    );
  }
  if (t === "phone") {
    return (
      <Input
        type="tel"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="+91…"
        className="mt-1 rounded-sm font-mono-data"
        data-testid={tid}
      />
    );
  }
  if (t === "boolean") {
    return (
      <div className="mt-2 inline-flex items-center gap-2">
        <Checkbox
          checked={value === true || value === "true"}
          onCheckedChange={(v) => onChange(!!v)}
          data-testid={tid}
        />
        <span className="text-xs text-slate-600">{value === true || value === "true" ? "Yes" : "No"}</span>
      </div>
    );
  }
  if (t === "select") {
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className="mt-1 rounded-sm" data-testid={tid}>
          <SelectValue placeholder="Select…" />
        </SelectTrigger>
        <SelectContent>
          {(column.options || []).map((o) => (
            <SelectItem key={o} value={o}>{o}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (t === "multiselect") {
    const selected = Array.isArray(value) ? value : [];
    const toggle = (o) => {
      const next = selected.includes(o) ? selected.filter((x) => x !== o) : [...selected, o];
      onChange(next);
    };
    return (
      <div className="mt-1 flex flex-wrap gap-1.5" data-testid={tid}>
        {(column.options || []).length === 0 && (
          <div className="text-[11px] text-slate-400">No options defined</div>
        )}
        {(column.options || []).map((o) => {
          const on = selected.includes(o);
          return (
            <button
              type="button"
              key={o}
              onClick={() => toggle(o)}
              className={`px-2 py-1 rounded-sm text-xs border ${
                on
                  ? "bg-[#0a2540] text-white border-[#0a2540]"
                  : "bg-white text-slate-600 border-slate-200 hover:border-[#0a2540]"
              }`}
              data-testid={`${tid}-opt-${o}`}
            >
              {o}
            </button>
          );
        })}
      </div>
    );
  }
  // default = text
  return (
    <Input
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 rounded-sm"
      data-testid={tid}
    />
  );
}

export function FieldDisplay({ column, value }) {
  const t = column.type || "text";
  if (value === undefined || value === null || value === "") return <span className="text-slate-400">—</span>;
  if (t === "url") {
    return (
      <a href={value} target="_blank" rel="noreferrer" className="text-[#0a2540] hover:underline break-all">
        {value}
      </a>
    );
  }
  if (t === "email") {
    return <a href={`mailto:${value}`} className="text-[#0a2540] hover:underline">{value}</a>;
  }
  if (t === "phone") {
    return <a href={`tel:${value}`} className="text-[#0a2540] hover:underline font-mono-data">{value}</a>;
  }
  if (t === "boolean") {
    const on = value === true || value === "true";
    return (
      <span className={`status-pill ${on ? "status-completed" : "status-cancelled"}`}>
        {on ? "Yes" : "No"}
      </span>
    );
  }
  if (t === "multiselect") {
    const arr = Array.isArray(value) ? value : [];
    if (arr.length === 0) return <span className="text-slate-400">—</span>;
    return (
      <span className="inline-flex flex-wrap gap-1">
        {arr.map((v) => (
          <span key={v} className="px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded-sm text-[10px]">
            {v}
          </span>
        ))}
      </span>
    );
  }
  if (t === "date") {
    return <span className="font-mono-data text-[#0a2540]">{fmtDate(value)}</span>;
  }
  if (t === "reminder") {
    // Visual cue: highlight today / past dates so the user knows it's actionable.
    const today = new Date().toISOString().slice(0, 10);
    const isToday = value === today;
    const isPast = value && value < today;
    return (
      <span
        className={`inline-flex items-center gap-1 font-mono-data ${
          isToday ? "text-red-700 font-semibold" : isPast ? "text-slate-400 line-through" : "text-[#0a2540]"
        }`}
        title={isToday ? "Reminder is today" : isPast ? "Past — reminder already fired" : "Upcoming reminder"}
      >
        🔔 {fmtDate(value)}
      </span>
    );
  }
  if (t === "number") {
    return <span className="font-mono-data">{value}</span>;
  }
  if (t === "longtext") {
    return <span className="whitespace-pre-wrap">{value}</span>;
  }
  return <span>{value}</span>;
}
